import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AdminUser } from '../lib/authService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Calendar,
  Users,
  Plus,
  Loader2,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

interface FastingEvent {
  id: string
  type: 'personal' | 'church'
  church_id: string
  created_by: string
  title: string
  description: string
  fasting_type: 'full' | 'partial' | 'daniel' | 'water_only' | 'custom'
  custom_rules?: string
  start_date: string
  end_date: string
  start_time?: string
  end_time?: string
  reminder_enabled: boolean
  reminder_times: string[]
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
  created_at: string
  participant_count?: number
}

interface FastingManagementProps {
  user: AdminUser | null
}

export default function FastingManagement({ user: currentUser }: FastingManagementProps) {
  const { features, limits, loading: subscriptionLoading } = useSubscription(currentUser?.churchId)
  const [fastingEvents, setFastingEvents] = useState<FastingEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fasting_type: 'daniel' as const,
    custom_rules: '',
    start_date: '',
    end_date: '',
    start_time: '06:00',
    end_time: '18:00',
    reminder_enabled: true,
    reminder_times: ['06:00', '12:00', '18:00']
  })

  useEffect(() => {
    loadFastingEvents()
  }, [currentUser])

  const loadFastingEvents = async () => {
    if (!currentUser?.churchId) return

    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('fasting_events')
        .select(`
          *,
          fasting_participants(count)
        `)
        .eq('type', 'church')
        .eq('church_id', currentUser.churchId)
        .order('start_date', { ascending: false })

      if (error) throw error

      const eventsWithCount = (data || []).map((event: any) => ({
        ...event,
        participant_count: event.fasting_participants?.[0]?.count || 0
      }))

      setFastingEvents(eventsWithCount)
    } catch (error) {
      console.error('Error loading fasting events:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateFasting = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!currentUser?.churchId || !currentUser?.id) {
      alert('Missing user information')
      return
    }

    try {
      const { error } = await supabase
        .from('fasting_events')
        .insert({
          type: 'church',
          church_id: currentUser.churchId,
          created_by: currentUser.id,
          title: formData.title,
          description: formData.description,
          fasting_type: formData.fasting_type,
          custom_rules: formData.custom_rules || null,
          start_date: formData.start_date,
          end_date: formData.end_date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          reminder_enabled: formData.reminder_enabled,
          reminder_times: formData.reminder_times
        })

      if (error) throw error

      setShowCreateModal(false)
      setFormData({
        title: '',
        description: '',
        fasting_type: 'daniel',
        custom_rules: '',
        start_date: '',
        end_date: '',
        start_time: '06:00',
        end_time: '18:00',
        reminder_enabled: true,
        reminder_times: ['06:00', '12:00', '18:00']
      })

      await loadFastingEvents()
    } catch (error) {
      console.error('Error creating fasting event:', error)
      alert('Failed to create fasting event')
    }
  }

  const handleCancelFasting = async (eventId: string) => {
    if (!confirm('Are you sure you want to cancel this fasting event?')) return

    try {
      const { error } = await supabase
        .from('fasting_events')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', eventId)

      if (error) throw error
      await loadFastingEvents()
    } catch (error) {
      console.error('Error cancelling fasting event:', error)
      alert('Failed to cancel fasting event')
    }
  }

  const getFastingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      full: 'Full Fast (No Food/Water)',
      partial: 'Partial Fast (Skip Meals)',
      daniel: 'Daniel Fast (Vegetables Only)',
      water_only: 'Water Only',
      custom: 'Custom Fast'
    }
    return labels[type] || type
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline' | 'destructive', icon: React.ReactNode }> = {
      upcoming: { variant: 'secondary', icon: <Clock className="h-3 w-3 mr-1" /> },
      active: { variant: 'default', icon: <AlertCircle className="h-3 w-3 mr-1" /> },
      completed: { variant: 'outline', icon: <CheckCircle className="h-3 w-3 mr-1" /> },
      cancelled: { variant: 'destructive', icon: <XCircle className="h-3 w-3 mr-1" /> }
    }
    return variants[status] || variants.upcoming
  }

  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    return `${days} ${days === 1 ? 'day' : 'days'}`
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  if (!features.advancedMinistry) {
    return (
      <FeatureRestricted
        featureName="Fasting Management"
        description="Organize and track church-wide and personal fasting campaigns with reminders."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="growth"
        icon={<Calendar className="h-10 w-10 text-white" />}
      />
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fasting Management</h1>
          <p className="text-muted-foreground">
            Create and manage church-wide fasting events
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Fasting Event
        </Button>
      </div>

      {/* Events List */}
      <div className="grid gap-4">
        {fastingEvents.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No fasting events yet</p>
              <p className="text-sm text-muted-foreground">Create your first church-wide fasting event</p>
            </CardContent>
          </Card>
        ) : (
          fastingEvents.map((event) => {
            const statusBadge = getStatusBadge(event.status)
            return (
              <Card key={event.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-xl">{event.title}</CardTitle>
                        <Badge variant={statusBadge.variant} className="flex items-center">
                          {statusBadge.icon}
                          {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                        </Badge>
                      </div>
                      <CardDescription>{event.description}</CardDescription>
                    </div>
                    {event.status === 'upcoming' && (
                      <Button
                        onClick={() => handleCancelFasting(event.id)}
                        variant="ghost"
                        size="sm"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Type</p>
                      <p className="font-medium">{getFastingTypeLabel(event.fasting_type)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Duration</p>
                      <p className="font-medium">{calculateDuration(event.start_date, event.end_date)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Start Date</p>
                      <p className="font-medium">{new Date(event.start_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">End Date</p>
                      <p className="font-medium">{new Date(event.end_date).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {event.start_time && event.end_time && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Daily:</span>
                      <span className="font-medium">{event.start_time} - {event.end_time}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Participants:</span>
                    <Badge variant="secondary">{event.participant_count || 0}</Badge>
                  </div>

                  {event.custom_rules && (
                    <Card className="bg-muted/50">
                      <CardContent className="p-4">
                        <p className="text-sm">
                          <span className="font-medium">Custom Rules: </span>
                          {event.custom_rules}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            )
          })
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>Create Fasting Event</DialogTitle>
            <DialogDescription>
              Create a church-wide fasting event for your congregation
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateFasting}>
            <div className="space-y-4 py-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., 3-Day Daniel Fast"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Purpose and details of this fasting event..."
                />
              </div>

              {/* Fasting Type */}
              <div className="space-y-2">
                <Label htmlFor="fasting_type">Fasting Type *</Label>
                <Select
                  required
                  value={formData.fasting_type}
                  onValueChange={(value) => setFormData({ ...formData, fasting_type: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daniel">Daniel Fast (Vegetables Only)</SelectItem>
                    <SelectItem value="partial">Partial Fast (Skip Meals)</SelectItem>
                    <SelectItem value="water_only">Water Only</SelectItem>
                    <SelectItem value="full">Full Fast (No Food/Water)</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Rules */}
              {formData.fasting_type === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom_rules">Custom Rules</Label>
                  <Textarea
                    id="custom_rules"
                    value={formData.custom_rules}
                    onChange={(e) => setFormData({ ...formData, custom_rules: e.target.value })}
                    rows={2}
                    placeholder="Describe the custom fasting rules..."
                  />
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    required
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Daily Time Range (for partial fasts) */}
              {formData.fasting_type === 'partial' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time">Start Time</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time">End Time</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                Create Fasting Event
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
